'use client';

import { optimizeBuilderImage } from '../../utils/optimizeBuilderImage';

const normalizeStops = (stops = []) =>
  stops
    .filter((stop) => stop && stop.color)
    .map((stop) => {
      const position =
        Number.isFinite(stop.position) && stop.position >= 0 && stop.position <= 100
          ? `${stop.position}%`
          : '';
      return position ? `${stop.color} ${position}` : stop.color;
    });

export const getBackgroundGradient = ({
  backgroundGradient,
  backgroundGradientEnabled,
  backgroundGradientType = 'linear',
  backgroundGradientDirection = 'to bottom',
  backgroundGradientAngle = 180,
  backgroundGradientStops = [],
} = {}) => {
  const rawGradient = typeof backgroundGradient === 'string' ? backgroundGradient.trim() : '';
  if (rawGradient) {
    return rawGradient;
  }

  if (!backgroundGradientEnabled) {
    return '';
  }

  const stops = normalizeStops(backgroundGradientStops);
  if (stops.length < 2) {
    return '';
  }

  if (backgroundGradientType === 'radial') {
    return `radial-gradient(circle at center, ${stops.join(', ')})`;
  }

  const direction =
    backgroundGradientDirection === 'custom-angle'
      ? `${Number.isFinite(backgroundGradientAngle) ? backgroundGradientAngle : 180}deg`
      : backgroundGradientDirection;

  return `linear-gradient(${direction}, ${stops.join(', ')})`;
};

/**
 * Section background CSS with author-overridable `backgroundSize` /
 * `backgroundPosition`.
 *
 * Defaults stay `'cover'` / `'center'` — pixel-identical to the prior
 * behavior for every existing campaign page. Authors can switch to
 * `'contain'` (no crop), `'auto'` (intrinsic size), or any custom value
 * (`'100% 100%'`, `'400px auto'`) to stop the background from re-cropping
 * every time padding / content reflow nudges the section height. That
 * re-crop is the "background zooms when I change horizontal padding"
 * symptom on `BuilderSplitSection`: `overflow: hidden` + `cover` recenters
 * the image around the new section aspect ratio after each reflow.
 *
 * Also sets `backgroundRepeat: 'no-repeat'` defensively so that authors
 * who pick `'contain'` or a fixed pixel size don't get the image tiled.
 */
export const getSectionBackgroundStyles = ({
  backgroundColor,
  backgroundGradient,
  backgroundImage,
  backgroundImageMobile,
  backgroundSize = 'cover',
  backgroundPosition = 'center',
}) => {
  const gradient = typeof backgroundGradient === 'string' ? backgroundGradient.trim() : '';
  const hasGradient = Boolean(gradient);
  const hasImage = Boolean(backgroundImage || backgroundImageMobile);

  if (hasImage) {
    const imageForXs = optimizeBuilderImage(backgroundImageMobile || backgroundImage, {
      width: 640,
    });
    const imageForSm = optimizeBuilderImage(backgroundImage || backgroundImageMobile, {
      width: 1920,
    });
    const withGradient = (url) => (hasGradient ? `${gradient}, url(${url})` : `url(${url})`);

    return {
      backgroundColor,
      backgroundImage: {
        xs: withGradient(imageForXs),
        sm: withGradient(imageForSm),
      },
      backgroundSize,
      backgroundPosition,
      backgroundRepeat: 'no-repeat',
      position: 'relative',
    };
  }

  if (hasGradient) {
    return {
      backgroundColor,
      backgroundImage: gradient,
      backgroundSize,
      backgroundPosition,
    };
  }

  return {
    backgroundColor,
  };
};

export const getScaledSpacing = (value) => {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return `calc(${numericValue}px * var(--builder-section-spacing, 1))`;
  }
  return value;
};

export const getScaledFontSize = (value) => {
  if (typeof value === 'number') {
    return `calc(${value}px * var(--builder-font-scale, 1))`;
  }
  if (typeof value !== 'string') {
    return value;
  }
  const match = value.trim().match(/^(-?\d*\.?\d+)([a-z%]+)$/i);
  if (!match) {
    return value;
  }
  const [, size, unit] = match;
  return `calc(${size}${unit} * var(--builder-font-scale, 1))`;
};
